import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiChevronDown, HiOutlineChatBubbleOvalLeft, HiPlus, HiSparkles } from 'react-icons/hi2';
import { LuPanelRightOpen } from 'react-icons/lu';
import { useAppInfo } from '../../helpers/hooks/common-hook';
import { isCopilotConfigured } from './api/copilot-api';
import { Spinner } from './components/activity';
import { AiUsageLimitLabel, AiUsageLimitNotice } from './components/ai-usage-limit';
import Composer from './components/composer';
import CopilotErrorBoundary from './components/copilot-error-boundary';
import { ACCOUNT_SUGGESTIONS, APP_SUGGESTIONS, ConversationError, SessionMenu } from './components/copilot-panel';
import MessageList from './components/message-list';
import ResourcePanel from './components/resource-panel';
import { hasInitialOnboarding, completeInitialOnboarding, KICKOFF_TEXT } from './model/onboarding';
import useCopilot from './hooks/use-copilot';
import useRouteAction from './hooks/use-route-action';
import { useCopilotWorkspace } from './copilot-workspace';
import useCopilotResources from './hooks/use-copilot-resources';
import useCopilotScope from './hooks/use-copilot-scope';
import { changesOf, copilotPathFor } from './model/copilot-paths';
import { awaitsTypedAnswer } from './model/clarification';
import useUnpublishedApp from './hooks/use-unpublished-app';
import PublishStepper, { SHOW_PUBLISH_STEPPER } from './components/publish-stepper';

const PANEL_KEY = 'bc-copilot-native:resources-open';

function readPanelOpen() {
    try {
        return window.localStorage.getItem(PANEL_KEY) !== '0';
    } catch (_) {
        return true;
    }
}

function PageNotice({ title, children, action }) {
    return (
        <div className="flex h-full items-center justify-center bg-white px-6">
            <div className="max-w-md text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-900 text-brand-lime">
                    <HiSparkles className="h-6 w-6" />
                </span>
                <h1 className="mt-4 text-lg font-semibold text-gray-900">{title}</h1>
                <p className="mt-1.5 text-sm text-gray-500">{children}</p>
                {action && <div className="mt-5">{action}</div>}
            </div>
        </div>
    );
}

/** The first screen of a conversation: a greeting, the message box and starter prompts. */
function Welcome({ isAccountScope, appName, composer, onPick }) {
    const suggestions = isAccountScope ? ACCOUNT_SUGGESTIONS : APP_SUGGESTIONS;
    return (
        <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center px-6 py-10">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-900 text-brand-lime shadow-sm">
                <HiSparkles className="h-5 w-5" />
            </span>
            <h1 className="mt-5 text-[1.6rem] font-semibold tracking-tight text-gray-900">
                {isAccountScope ? 'What would you like to build?' : 'What can I help you build?'}
            </h1>
            <p className="mt-1.5 text-[0.95rem] text-gray-500">
                {isAccountScope
                    ? 'Create a new agent, open one of yours, or ask anything about building agents.'
                    : `Describe what ${appName || 'your agent'} should do. I'll set up instructions, tools, playbooks, knowledge and integrations.`}
            </p>
            <div className="mt-6">{composer}</div>
            <ul className="mt-5 space-y-0.5" aria-label="Suggested prompts">
                {suggestions.map(suggestion => (
                    <li key={suggestion}>
                        <button
                            type="button"
                            onClick={() => onPick(suggestion)}
                            className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[0.925rem] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                        >
                            <HiOutlineChatBubbleOvalLeft className="h-[18px] w-[18px] shrink-0 text-gray-400 group-hover:text-gray-700" />
                            {suggestion}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * The Agent Builder Copilot as a full page (`/console` and `/app/{appId}/console`):
 * the conversation in the centre, and on the right the resources it is building.
 *
 * ROUTE actions inside the current scope stay as links in the reply, so the conversation
 * is never interrupted; an action into another scope (an application it just created)
 * opens that scope's copilot page with the handoff message pre-filled.
 */
export default function CopilotPage() {
    if (!isCopilotConfigured()) {
        return (
            <PageNotice title="Console isn't available">
                The console is not configured for this environment.
            </PageNotice>
        );
    }
    return (
        <CopilotErrorBoundary
            fallback={({ reset }) => (
                <PageNotice
                    title="Console hit a problem"
                    action={<button type="button" onClick={reset} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-primary">Try again</button>}
                >
                    Your agent is not affected.
                </PageNotice>
            )}
        >
            <CopilotPageInner />
        </CopilotErrorBoundary>
    );
}

function CopilotPageInner() {
    const workspace = useCopilotWorkspace();
    return workspace
        ? <CopilotPageContent copilot={workspace.copilot} scopeInfo={workspace.scopeInfo} stepperOpen={workspace.stepperOpen} />
        : <StandaloneCopilotPage />;
}

function StandaloneCopilotPage() {
    const scopeInfo = useCopilotScope();
    const onAction = useRouteAction(scopeInfo);
    const copilot = useCopilot(scopeInfo.scope, Boolean(scopeInfo.scope), { onAction });
    // The publish stepper is disabled for now (see SHOW_PUBLISH_STEPPER).
    const stepperOpen = useUnpublishedApp(scopeInfo.appId, SHOW_PUBLISH_STEPPER);
    return <CopilotPageContent copilot={copilot} scopeInfo={scopeInfo} stepperOpen={stepperOpen} />;
}

function CopilotPageContent({ copilot, scopeInfo, stepperOpen }) {
    const navigate = useNavigate();
    const location = useLocation();
    const appInfo = useAppInfo();
    const { scope, appId, isAccountScope, error: scopeError } = scopeInfo;
    const [menuOpen, setMenuOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(readPanelOpen);
    const onboarding = Boolean(appId) && new URLSearchParams(location.search).get('onboarding') === '1'
        && (hasInitialOnboarding(appId) || location.state?.initialOnboardingAppId === appId);

    const resources = useCopilotResources({ appId, isAccountScope });
    const changes = useMemo(() => changesOf(copilot.messages), [copilot.messages]);

    useEffect(() => {
        try {
            window.localStorage.setItem(PANEL_KEY, panelOpen ? '1' : '0');
        } catch (_) {
            // ignore
        }
    }, [panelOpen]);

    const ready = Boolean(scope);
    const empty = ready && !copilot.messages.length && !copilot.loadingHistory;

    const dropQuery = name => {
        const params = new URLSearchParams(location.search);
        params.delete(name);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true, state: null });
    };

    // A new application: start the copilot's onboarding playbook (its trigger text), once, in a fresh chat.
    const kickedOff = useRef(false);
    useEffect(() => {
        if (!onboarding || kickedOff.current || !copilot.historyLoaded || copilot.messages.length || copilot.running) return;
        kickedOff.current = true;
        completeInitialOnboarding(appId);
        dropQuery('onboarding');
        copilot.send(KICKOFF_TEXT);
        // Runs once per page: the query and the pending flag are cleared above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboarding, copilot.historyLoaded, copilot.messages.length, copilot.running]);

    // The "your agent is ready" email links to the onboarding conversation, possibly in another browser.
    const linkedSession = new URLSearchParams(location.search).get('copilotSession');
    useEffect(() => {
        if (!linkedSession || !appId) return;
        if (/^copilot-[A-Za-z0-9_-]{1,56}$/.test(linkedSession)) copilot.openSession(linkedSession);
        dropQuery('copilotSession');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedSession, appId]);
    const reconnecting = copilot.connection === 'reconnecting';
    const send = text => (copilot.awaitingApproval ? copilot.reject(text) : copilot.send(text));

    const composer = (
        <>
            <AiUsageLimitNotice aiUsage={copilot.aiUsage} />
            {copilot.error && !copilot.running && (
                <p className="mb-2 px-1 text-xs text-red-600 dark:text-red-400" role="alert">{String(copilot.error)}</p>
            )}
            <Composer
                autoFocus
                value={copilot.composerText}
                onChange={copilot.setComposerText}
                initialText={copilot.draft}
                onInitialTextUsed={copilot.clearDraft}
                onSend={send}
                disabled={copilot.running || !ready}
                placeholder={copilot.awaitingApproval
                    ? 'Reply to reject with instructions…'
                    : awaitsTypedAnswer(copilot.messages) ? 'Type your answer…'
                    : isAccountScope ? 'Ask Copilot…' : `Ask Copilot to build ${appInfo?.name || 'your agent'}…`}
            />
        </>
    );

    return (
        <div className="flex h-full min-h-0 flex-col bg-white lg:flex-row">
            {stepperOpen && <PublishStepper />}
            <div className="flex min-h-0 min-w-0 flex-1">
                <main className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <header className="relative flex h-[72px] shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4">
                        <button
                            type="button"
                            onClick={() => setMenuOpen(open => !open)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            disabled={!ready}
                            className="flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-gray-50 disabled:opacity-60"
                        >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-brand-lime">
                                <HiSparkles className="h-3.5 w-3.5" />
                            </span>
                            <span className="flex min-w-0 flex-col items-start leading-tight">
                                <span className="text-[0.925rem] font-semibold text-gray-900">Console</span>
                                <span className="truncate text-[11px] text-gray-500">
                                    {isAccountScope ? 'All applications' : (appInfo?.name || 'This agent')}
                                </span>
                            </span>
                            <HiChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                        {menuOpen && (
                            <CopilotErrorBoundary fallback={null} onError={() => setMenuOpen(false)}>
                                <SessionMenu copilot={copilot} onClose={() => setMenuOpen(false)} className="absolute left-4 top-full" />
                            </CopilotErrorBoundary>
                        )}
                        <div className="flex items-center gap-1.5">
                            <AiUsageLimitLabel aiUsage={copilot.aiUsage} />
                            {reconnecting && (
                                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" /> Reconnecting…
                                </span>
                            )}
                            {copilot.running && !reconnecting && (
                                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-lime" /> Working…
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={copilot.newChat}
                                disabled={!ready || copilot.running}
                                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
                            >
                                <HiPlus className="h-3.5 w-3.5" /> New chat
                            </button>
                            {!panelOpen && (
                                <button
                                    type="button"
                                    onClick={() => setPanelOpen(true)}
                                    aria-label="Show resources"
                                    title="Show resources"
                                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                                >
                                    <LuPanelRightOpen className="h-[18px] w-[18px]" />
                                </button>
                            )}
                        </div>
                    </header>

                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                        <CopilotErrorBoundary
                            resetKeys={[copilot.sessionId]}
                            fallback={({ reset }) => <div className="mx-auto w-full max-w-3xl"><ConversationError reset={reset} onNewChat={copilot.newChat} /></div>}
                        >
                            {!ready
                                ? (
                                    <div className="flex flex-1 items-center justify-center px-6">
                                        <p className="flex items-center gap-2 text-center text-sm text-gray-500">
                                            {!scopeError && <Spinner className="h-4 w-4" />}
                                            {scopeError || 'Loading your account…'}
                                        </p>
                                    </div>
                                )
                                : empty && !onboarding
                                    ? <Welcome isAccountScope={isAccountScope} appName={appInfo?.name} composer={composer} onPick={copilot.send} />
                                    : (
                                        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-2">
                                            <MessageList
                                                messages={Array.isArray(copilot.messages) ? copilot.messages : []}
                                                running={copilot.running}
                                                loading={copilot.loadingHistory}
                                                onApprove={copilot.approve}
                                                onReject={copilot.reject}
                                                onSuggestion={copilot.send}
                                                onUploaded={copilot.uploaded}
                                                uploadAppId={copilot.appId}
                                                navigationVerb="Open"
                                            />
                                        </div>
                                    )}
                        </CopilotErrorBoundary>
                    </div>

                    {ready && !empty && (
                        <div className="shrink-0 border-t border-slate-200/70 bg-white px-4 pb-4 pt-3">
                            <div className="mx-auto w-full max-w-3xl">
                                {composer}
                                <p className="mt-2 text-center text-[11px] text-gray-500">
                                    {isAccountScope
                                        ? 'Copilot works with your applications here. Open one to build it.'
                                        : 'Copilot changes your draft agent. Publish to make changes live.'}
                                </p>
                            </div>
                        </div>
                    )}
                </main>

                {panelOpen && (
                    <ResourcePanel
                        appId={appId}
                        appInfo={appInfo}
                        isAccountScope={isAccountScope}
                        resources={resources}
                        changes={changes}
                        copilotPathFor={copilotPathFor}
                        onCollapse={() => setPanelOpen(false)}
                        onPublish={appId ? () => navigate(`/app/${appId}/versions`, { state: { focusPublish: true, fromCopilot: true } }) : null}
                        onSimulate={appId ? () => navigate(`/app/${appId}/agent-preview`) : null}
                    />
                )}
            </div>
        </div>
    );
}
